import { type FormikProps } from "formik";
import React from "react";
import { type SurveyContentView } from "../../types/state";
import { ValidatedForm } from "../../../../../components/forms";
import { type SurveyContentFormProps } from "../types";
import QuestionForm from "features/Library/Common/QuestionsForm/QuestionForm";
import { SurveyQuestionBuilder, SurveyAnswersBuilder } from "./SurveyBuilders";
import QuestionsBuilder from "features/Library/Common/QuestionsForm/Questions/QuestionsBuilder/QuestionsBuilder";
import { SurveyQuestionHeader } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyHeader/SurveyQuestionHeader/SurveyQuestionHeader";
import validationSchemas from "../../../../../utils/validationSchemas";
import { getQuestionDefaultData } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyProviders/defaultQuestionProvider";
import { WithSelfValidation } from "../../../Common/Fields/WithSelfValidation";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import * as validationActions from "../../state/actions/surveyContentValidationActions";
import { bindActionCreators } from "redux";
import { surveysStateSelector } from "../../state/surveyReducer";
import { useFormikValidation } from "../../../Common/Hooks/useFormikValidationHook";

import { ContentContextProvider } from "./state/ContentContextProvider";
import { ViewContentLinkButton } from "components";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";

export type ContentFormPropsAll = SurveyContentFormProps & FormikProps<SurveyContentView> & PropsFromRedux;

export const SurveyContentForm = (props: ContentFormPropsAll) => {
  const {
    isLoading,
    errors,
    activeErrors,
    validateActions,
    isFormValid,
    touchedSnapshot,
    resolved,
    initialized,
    values,
  } = props;

  useFormikValidation({ errors, resolved, initialized, activeErrors, isFormValid }, { ...validateActions });

  const renderFields = (): React.ReactNode => {
    const { onBlur, save, disabled } = props;

    return (
      <ContentContextProvider>
        <WithSelfValidation
          {...props}
          validationSchema={validationSchemas.surveysContent}
          render={(validatedFieldProps) => (
            <QuestionForm
              renderQuestionBuilder={(arrayHelpers) => (
                <QuestionsBuilder
                  defaultDataProvider={getQuestionDefaultData}
                  onBlur={onBlur}
                  onChanged={save}
                  questions={values.questions}
                  arrayHelpers={arrayHelpers}
                  validatedFieldProps={{ ...validatedFieldProps, touched: !resolved ? touchedSnapshot : {} }}
                  disabled={disabled}
                  QuestionRenderer={SurveyQuestionBuilder}
                  AnswersBuilder={SurveyAnswersBuilder}
                  QuestionHeader={SurveyQuestionHeader}
                  propertyNamePrefix={""}
                />
              )}
            />
          )}
        />
      </ContentContextProvider>
    );
  };

  return (
    <>
      {props.hasBeenPublished && (
        <ViewContentLinkButton href={getContentLinkToEupFromPreview["Surveys"](props.id)} fitted />
      )}
      <ValidatedForm
        dirty={props.dirty}
        isLoading={isLoading}
        formWidthComputer={16}
        withoutFormik
        disablePreventTransitionPrompt
      >
        {renderFields}
      </ValidatedForm>
    </>
  );
};
/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    ...surveysStateSelector(state).base.surveysContentValidationReducer,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  validateActions: bindActionCreators(validationActions, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(SurveyContentForm);
export default ConnectedComponent;
